// The bakery as a footprint-native 2x2 plot: a half-timbered bakehouse on the
// left with a tall stone oven chimney and a glowing oven mouth, and an open shop
// terrace on the right (a canopied counter of golden loaves, flour sacks and a
// bistro table). Ported 1:1 from age-of-mcp's bakery.ts: local part generators
// plus the shared foundationSlab/scaffold and barrel/table props.
import * as THREE from "three";
import { defineModel, benchWork, foundationSlab, scaffold, barrel, table, smokePlume, firePulse, EFFECT_LOOP, type Part } from "../../lib";

const PLASTER = "#e3d5b8";
const TIMBER = "#6e4a2a";
const ROOF = "#a85a3a";
const RIDGE = "#7a3f28";
const STONE = "#8c847a";
const OVEN = "#ef8a3a";
const EMBER = "#f6c14a";
const ARCH = "#4f3220";
const BREAD = "#c89a52";
const AWN_A = "#efe6cf";
const AWN_B = "#bf4a36";
const WOOD = "#8a5e34";
const SACK = "#d8ccae";

const BASE = 0.14;
const BX = -0.46, BW = 0.92, BD = 1.4;
const FRONT = BD / 2;

function bakehouse(h: number): Part {
  return { geometry: new THREE.BoxGeometry(BW, h, BD), color: PLASTER, position: [BX, BASE + h / 2, 0] };
}

function chimney(h: number): Part {
  return { geometry: new THREE.BoxGeometry(0.2, h, 0.2), color: STONE, position: [BX + 0.26, BASE + h / 2, -BD / 2 + 0.18] };
}

// The "oven is lit" cue: smoke rising from the chimney mouth (the finished
// chimney tops out at y = BASE + 1.46) while the oven embers pulse.
const plume = smokePlume([BX + 0.26, BASE + 1.46, -BD / 2 + 0.18], { rise: 0.42 });

function framing(wallH: number): Part[] {
  const top = BASE + wallH;
  const x0 = BX - BW / 2, x1 = BX + BW / 2, zf = FRONT, zb = -FRONT;
  const post = (x: number, z: number): Part => ({
    geometry: new THREE.BoxGeometry(0.07, wallH, 0.07), color: TIMBER, position: [x, BASE + wallH / 2, z],
  });
  return [
    post(x0, zf), post(x1, zf), post(x0, zb), post(x1, zb),
    { geometry: new THREE.BoxGeometry(BW, 0.06, 0.05), color: TIMBER, position: [BX, top, zf + 0.01] },
    { geometry: new THREE.BoxGeometry(BW, 0.06, 0.05), color: TIMBER, position: [BX, BASE + wallH * 0.52, zf + 0.01] },
    { geometry: new THREE.BoxGeometry(0.06, wallH, 0.05), color: TIMBER, position: [BX - 0.2, BASE + wallH / 2, zf + 0.01] },
  ];
}

function roof(wallH: number): Part[] {
  const top = BASE + wallH;
  const rise = 0.5, halfD = BD / 2 + 0.08;
  const slope = Math.hypot(halfD, rise), ang = Math.atan2(rise, halfD);
  const w = BW + 0.12;
  return [
    { geometry: new THREE.BoxGeometry(w, 0.07, slope), color: ROOF, position: [BX, top + rise / 2, halfD / 2], rotation: [ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(w, 0.07, slope), color: ROOF, position: [BX, top + rise / 2, -halfD / 2], rotation: [-ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(w + 0.05, 0.08, 0.08), color: RIDGE, position: [BX, top + rise, 0] },
  ];
}

function bakehouseFront(): Part[] {
  const zf = FRONT + 0.03;
  return [
    { geometry: new THREE.BoxGeometry(0.28, 0.6, 0.05), color: ARCH, position: [BX - 0.26, BASE + 0.3, zf] },
    { geometry: new THREE.BoxGeometry(0.34, 0.32, 0.06), color: ARCH, position: [BX + 0.18, BASE + 0.3, zf] },
    { geometry: new THREE.BoxGeometry(0.24, 0.2, 0.07), color: OVEN, position: [BX + 0.18, BASE + 0.29, zf + 0.01] },
    { geometry: new THREE.BoxGeometry(0.13, 0.11, 0.08), color: EMBER, position: [BX + 0.18, BASE + 0.27, zf + 0.02], name: "embers" },
  ];
}

function shopStall(): Part[] {
  const cx = 0.5, cz = 0.42;
  const counterTop = BASE + 0.4;
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(0.06, 0.74, 0.06), color: WOOD, position: [cx - 0.28, BASE + 0.37, cz - 0.18] },
    { geometry: new THREE.BoxGeometry(0.06, 0.74, 0.06), color: WOOD, position: [cx + 0.28, BASE + 0.37, cz - 0.18] },
    { geometry: new THREE.BoxGeometry(0.66, 0.1, 0.22), color: WOOD, position: [cx, counterTop, cz + 0.04] },
    { geometry: new THREE.BoxGeometry(0.66, 0.3, 0.05), color: WOOD, position: [cx, BASE + 0.2, cz + 0.14] },
  ];
  for (let i = 0; i < 4; i++) {
    parts.push({
      geometry: new THREE.SphereGeometry(0.06, 7, 5), color: BREAD,
      position: [cx - 0.22 + i * 0.15, counterTop + 0.08, cz + 0.04], scale: [1, 0.7, 1.4],
    });
  }
  const stripes = [AWN_A, AWN_B, AWN_A, AWN_B, AWN_A];
  for (let i = 0; i < stripes.length; i++) {
    parts.push({
      geometry: new THREE.BoxGeometry(0.14, 0.04, 0.4), color: stripes[i]!,
      position: [cx - 0.28 + i * 0.14, BASE + 0.72, cz + 0.06], rotation: [0.4, 0, 0],
    });
  }
  return parts;
}

function flourSacks(x: number, z: number): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.18, 0.2, 0.18), color: SACK, position: [x, BASE + 0.1, z], rotation: [0, 0.25, 0] },
    { geometry: new THREE.BoxGeometry(0.16, 0.18, 0.16), color: SACK, position: [x + 0.04, BASE + 0.28, z - 0.03], rotation: [0, -0.2, 0] },
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(1.94)];
  if (s === 1) return [foundationSlab(1.94), bakehouse(0.4), chimney(0.7), ...scaffold(1.0, 0.9)];
  if (s === 2) return [foundationSlab(1.94), bakehouse(0.78), chimney(1.1), ...scaffold(1.0, 1.5)];

  const wallH = 0.86;
  return [
    foundationSlab(1.94),
    bakehouse(wallH),
    ...framing(wallH),
    ...roof(wallH),
    chimney(1.46),
    ...plume.parts,
    ...bakehouseFront(),
    ...shopStall(),
    ...flourSacks(0.16, -0.5),
    barrel(0.86, -0.1),
    ...table(0.66, -0.62),
  ];
}

export const bakery = defineModel({
  id: "bakery",
  tiles: { w: 2, h: 2 },
  batchId: "standard",
  name: "Bakery",
  tags: ["building","bakery","low-poly","age-of-mcp","classic","animated"],
  summary: "Low-poly bakery.",
  description: "A baker's oven that bakes flour and firewood into bread.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [
    {
      id: "active",
      label: "Oven lit",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [...plume.tracks, firePulse("embers", [BX + 0.18, BASE + 0.27, FRONT + 0.05])],
    },
  ],
  stations: [
    { id: "oven", position: [-0.28, 0.14, 0.9], facing: Math.PI, description: "At the oven." },
    { id: "table", position: [0.5, 0.14, 0.06], facing: 0, description: "At the kneading table." },
  ],
  interactions: [
    { id: "bake", label: "At the oven", station: "oven", variants: ["complete"], ...benchWork() },
  ],
});
